'use client'

import { useState } from 'react'
import { ColumnDef } from "@tanstack/react-table"
import { ArrowUpDown, MoreHorizontal, Building2, Phone, Mail, MapPin, Trash2, ExternalLink, User, Users, Building } from "lucide-react"
import { Button } from "@/components/ui/button"
import { EditCustomerForm } from "./EditCustomerForm"
import FormModal from "@/components/FormModal"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { useToast } from '@/hooks/use-toast'
import CustomerService from '@/api/customers.service'
 
// This type is used to define the shape of our data.
export type Customer = {
  publicId: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  emailAddress: string;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  businessName?: string;
}

export const columns: ColumnDef<Customer>[] = [
  {
    accessorKey: "name",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="pl-0 font-medium text-sm"
      >
        Customer
        <ArrowUpDown className="w-4 h-4 ml-2" />
      </Button>
    ),
    cell: ({ row }) => {
      const customer = row.original;
      const initials = `${customer.firstName.charAt(0)}${customer.lastName.charAt(0)}`.toUpperCase();
      
      return (
        <div className="flex items-center space-x-3">
          <Avatar className="h-9 w-9 bg-primary/10">
            <AvatarFallback className="text-primary font-medium">{initials}</AvatarFallback>
          </Avatar>
          <div>
            <div className="font-medium">
              {customer.firstName} {customer.lastName}
            </div>
            {customer.businessName && (
              <div className="text-xs text-muted-foreground flex items-center">
                <Building className="h-3 w-3 mr-1" />
                {customer.businessName}
              </div>
            )}
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "contact",
    header: "Contact Information",
    cell: ({ row }) => {
      const customer = row.original;
      return (
        <div className="space-y-1">
          <div className="text-sm flex items-center">
            <Mail className="h-3.5 w-3.5 mr-2 text-muted-foreground" />
            <a href={`mailto:${customer.emailAddress}`} className="text-primary hover:underline">
              {customer.emailAddress}
            </a>
          </div>
          <div className="text-sm flex items-center">
            <Phone className="h-3.5 w-3.5 mr-2 text-muted-foreground" />
            <a href={`tel:${customer.phoneNumber}`} className="hover:underline">
              {customer.phoneNumber}
            </a>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "location",
    header: "Location",
    cell: ({ row }) => {
      const customer = row.original;
      return (
        <div className="text-sm">
          <div className="flex items-start">
            <MapPin className="h-3.5 w-3.5 mr-2 mt-0.5 text-muted-foreground" />
            <div>
              <div>{customer.street}</div>
              <div>{customer.city}, {customer.state} {customer.zipCode}</div>
              <div>{customer.country}</div>
            </div>
          </div>
        </div>
      );
    },
  },
  {
    id: "actions",
    cell: ({ row }) => {
      const [isEditModalOpen, setEditModalOpen] = useState(false);
      const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
      const { toast } = useToast();
      const customer = row.original;

      const handleDelete = async () => {
        try {
          const response = await CustomerService.deleteCustomer(customer.publicId);
          if (response.data.result === 'success') {
            toast({
              title: "Customer deleted",
              description: `${customer.firstName} ${customer.lastName} has been removed successfully.`,
              duration: 3000,
            });
            setDeleteModalOpen(false);
            // Optionally, you can refresh the data here
            window.location.reload();
          } else {
            toast({
              title: "Deletion failed",
              description: response.data.message,
              variant: "destructive",
            });
          }
        } catch (error) {
          toast({
            title: "Error",
            description: "An error occurred while deleting the customer",
            variant: "destructive",
          });
        }
      };

      return (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            className="text-primary hover:text-primary/80 hover:bg-primary/10 mr-2"
            onClick={() => window.location.href = `/customer/${customer.publicId}`}
          >
            <ExternalLink className="h-4 w-4" />
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[160px]">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                className="cursor-pointer flex items-center"
                onClick={() => setEditModalOpen(true)}
              >
                <User className="w-4 h-4 mr-2" />
                Edit Customer
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="cursor-pointer text-destructive focus:text-destructive flex items-center"
                onClick={() => setDeleteModalOpen(true)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <FormModal
            open={isEditModalOpen}
            setOpen={setEditModalOpen}
            title="Update Customer"
          >
            <EditCustomerForm customer={customer} />
          </FormModal>
          
          <FormModal
            open={isDeleteModalOpen}
            setOpen={setDeleteModalOpen}
            title="Delete Customer"
          >
            <div className="p-6 space-y-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-full bg-destructive/10">
                  <Trash2 className="h-5 w-5 text-destructive" />
                </div>
                <div>
                  <h3 className="font-medium">Confirm Deletion</h3>
                  <p className="text-sm text-muted-foreground">
                    Are you sure you want to delete {customer.firstName} {customer.lastName}?
                    This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex justify-end space-x-2 pt-2">
                <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={handleDelete}>
                  Delete Customer
                </Button>
              </div>
            </div>
          </FormModal>
        </div>
      );
    },
  },
]
